import {
  format,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  parseISO,
  addDays,
  startOfDay,
  subDays,
  startOfWeek,
  startOfMonth,
  subMonths,
  formatISO,
} from "date-fns";
import { zeroDate } from "src/constants";

export const validEmail = (val: string): boolean =>
  Boolean(
    val &&
      /^[a-zA-Z0-9._%+-]+@(?!(?:[-.])|.*\.\.)[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/.test(
        val,
      ),
  );

export const validName = (val: string): boolean =>
  Boolean(val && /^[A-Za-zÀ-ÖØ-öø-ÿ '-]+$/.test(val));

export const validPhoneNumber = (value: string): boolean =>
  /^\d{10}$/.test(value);

export const addCountryCode = (phoneNumber: string): string =>
  phoneNumber.startsWith("+1") ? phoneNumber : `+1${phoneNumber}`;

export const shortMonthFromDate = (dateString) => {
  if (!dateString) return "";
  return format(addDays(parseISO(dateString), 1), "MMM");
};

export const dayOfMonthFromDate = (dateString) => {
  if (!dateString) return "";
  return format(addDays(parseISO(dateString), 1), "d");
};

export const dayAndDateFromDate = (dateString) => {
  if (!dateString || dateString === zeroDate) return "";
  return format(addDays(parseISO(dateString), 1), "EEEE, MMMM dd");
};
export const timeAgo = (dateString) => {
  if (dateString === zeroDate) return "----";
  const date = new Date(dateString);
  const now = new Date();

  const diffInYearsVal = differenceInYears(now, date);
  if (diffInYearsVal >= 1) {
    return format(date, "MM-dd-yyyy");
  }

  const diffInMonthsVal = differenceInMonths(now, date);
  if (diffInMonthsVal >= 1) {
    return `${diffInMonthsVal} month${diffInMonthsVal > 1 ? "s" : ""} ago`;
  }

  const diffInDaysVal = differenceInDays(now, date);
  if (diffInDaysVal >= 1) {
    return `${diffInDaysVal} day${diffInDaysVal > 1 ? "s" : ""} ago`;
  }

  const diffInHoursVal = differenceInHours(now, date);
  if (diffInHoursVal >= 1) {
    return `${diffInHoursVal} hour${diffInHoursVal > 1 ? "s" : ""} ago`;
  }

  const diffInMinutesVal = differenceInMinutes(now, date);
  if (diffInMinutesVal >= 1) {
    return `${diffInMinutesVal} minute${diffInMinutesVal > 1 ? "s" : ""} ago`;
  }

  return "Just now";
};

export const dateRangefromDatePeriod = (period) => {
  const now = new Date();
  const end = formatISO(now, { representation: "date" }) + "T23:59:59.000Z";
  let start;

  switch (period) {
    case "Today":
      start =
        formatISO(startOfDay(now), { representation: "date" }) +
        "T00:00:00.000Z";
      break;
    case "Yesterday":
      start =
        formatISO(startOfDay(subDays(now, 1)), { representation: "date" }) +
        "T00:00:00.000Z";
      break;
    case "This week":
      start =
        formatISO(startOfWeek(now, { weekStartsOn: 0 }), {
          representation: "date",
        }) + "T00:00:00.000Z"; // Assuming week starts on Sunday
      break;
    case "This month":
      start =
        formatISO(startOfMonth(now), { representation: "date" }) +
        "T00:00:00.000Z";
      break;
    case "Last month":
      start =
        formatISO(startOfMonth(subMonths(now, 1)), { representation: "date" }) +
        "T00:00:00.000Z";
      break;
    default:
      return {
        end: null,
        start: null,
        error: `Invalid period: "${period}". Please use "Today", "Yesterday", "This week", "This month", or "Last month".`,
      };
  }

  return {
    end,
    start,
    period,
  };
};

export const formatDateRangeShort = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return "";
  }
  const parsedStartDate = addDays(parseISO(startDate), 1); // addDays is a dumb hack for UTC conversion
  const parsedEndDate = addDays(parseISO(endDate), 1); // addDays is a dumb hack for UTC conversion

  if (isNaN(parsedEndDate.getTime()) || isNaN(parsedEndDate.getTime())) {
    return ""; // Handle invalid date strings
  }

  const startMonth = format(parsedStartDate, "MMM");
  const startDay = format(parsedStartDate, "d");
  const endDay = format(parsedEndDate, "d");

  return `${startMonth} ${startDay}-${endDay}`;
};

// Can be used to create a date string in the format 'YYYY-MM-DD'
// Can also be used for the above function issues with UTC conversion.
export const createIsoDate = (
  year: number,
  month: number,
  day: number = 1,
): string => {
  const date = new Date(Date.UTC(year, month - 1, day)); // Create UTC date
  return date.toISOString(); // Stores as 'YYYY-MM-DDT00:00:00.000Z'
};

export const formatNumberForCurrency = (num: number): string => {
  return Number(num).toLocaleString("en", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
